import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { startOfMonth, subMonths } from 'date-fns';
import { AnyAction } from 'redux';

import {
  AppState,
  reportFilterStartOperationDateSelector,
  reportFilterEndOperationDateSelector,
  changeFilterStartOperationDate,
  changeFilterEndOperationDate,
  filterParametersDataSelector,
} from '../../../state';
import { constants, isEqualNullableDate } from '../../../helpers';
import DateRangeFlexSales, {
  DateRangeFlexSalesProps,
  DateRangeFlexSalesValue,
} from '../../DateRangeFlexSales';

type DateSelectorValue = {
  raw: object | null | undefined;
  accept: Date | null | undefined;
};

type OperationDateRangeFilterProps = Pick<DateRangeFlexSalesProps<Date>, 'spacing'> & {
  maxDaysBetween?: number; // Nueva prop opcional
  requiredIncomplete?: boolean;
  maxDate?: Date;
  startDateSelector?: (state: AppState) => DateSelectorValue;
  endDateSelector?: (state: AppState) => DateSelectorValue;
  changeStartDateAction?: (value: Date | null) => AnyAction;
  changeEndDateAction?: (value: Date | null) => AnyAction;
};

const OperationDateRangeFilter = ({
  maxDate,
  startDateSelector = reportFilterStartOperationDateSelector,
  endDateSelector = reportFilterEndOperationDateSelector,
  changeStartDateAction = changeFilterStartOperationDate,
  changeEndDateAction = changeFilterEndOperationDate,
  ...props
}: OperationDateRangeFilterProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const startDate = useSelector(startDateSelector);
  const endDate = useSelector(endDateSelector);
  const filterParameters = useSelector(filterParametersDataSelector);
  const minDate = startOfMonth(subMonths(new Date(), filterParameters?.filtersLimitMonths || 3));

  const dateValue: DateRangeFlexSalesValue<Date> = useMemo(
    () => [startDate.raw, endDate.raw] as DateRangeFlexSalesValue<Date>,
    [startDate.raw, endDate.raw]
  );

  const handleChange = useCallback(
    ([newStartDate, newEndDate]: DateRangeFlexSalesValue<Date>) => {
      if (!isEqualNullableDate(newStartDate, startDate.raw as Date)) {
        dispatch(changeStartDateAction(newStartDate));
      } else if (!isEqualNullableDate(newEndDate, endDate.raw as Date))
        dispatch(changeEndDateAction(newEndDate));
    },
    [dispatch, endDate, startDate, changeStartDateAction, changeEndDateAction]
  );

  return (
    <DateRangeFlexSales
      {...props}
      maxDate={maxDate || constants.reportFilterRangeMaxDate}
      minDate={minDate}
      startText={t('toolbar.labels.fromDateOperation')}
      endText={t('toolbar.labels.toDateOperation')}
      value={dateValue}
      onChange={handleChange}
    />
  );
};

export default OperationDateRangeFilter;
